import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Search, Eye, ArrowLeft, Upload, X, GripVertical, FileDown, ChefHat, ListChecks } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATS = ["Soup / Kuah", "Condiment / Saus", "Sayuran", "Daging", "Ikan & Seafood", "Bakso & Olahan", "Minuman"];
const STORAGE_OPTS = ["Suhu Ruangan", "Chiller", "Freezer", "Showcase"];
const photoUrl = (id) => (id ? `${BACKEND}/api/recipes/photo/${id}` : "");
const EMPTY = { name: "", category: CATS[0], description: "", batch: "", photo_product: "", photo_final: "", ingredients: [], steps: [], qc: [], storage_location: STORAGE_OPTS[0], shelf_life: "", notes: "" };

function PhotoSlot({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (fl) => {
    const f = fl?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("File harus gambar"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Maksimal 8MB"); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/recipes/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.id); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-muted-foreground">{label}</div>
      {value ? (
        <div className="group relative h-32 w-full overflow-hidden rounded-xl border border-neutral-200">
          <img src={photoUrl(value)} alt="" className="h-full w-full object-cover" />
          <button data-testid={`${testid}-remove`} onClick={() => onChange("")} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg bg-black/60 text-white hover:bg-crimson"><X size={14} /></button>
        </div>
      ) : (
        <label data-testid={testid} className="flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-neutral-300 bg-neutral-100 text-xs text-muted-foreground hover:bg-neutral-200">
          <Upload size={18} /> {busy ? "Mengunggah…" : "Unggah Foto"}
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => pick(e.target.files)} />
        </label>
      )}
    </div>
  );
}

export function RecipePanel() {
  const [view, setView] = useState("list");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [fcat, setFcat] = useState("all");
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragIdx = useRef(null);

  const load = () => api.get("/recipes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setView("form"); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setView("form"); };
  const openDetail = (r) => { setDetail(r); setView("detail"); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Isi nama produk"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/recipes/${editId}`, form); else await api.post("/recipes", form);
      toast.success(editId ? "Resep diperbarui" : "Resep & SOP ditambahkan");
      setView("list"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const doDelete = async () => {
    const id = confirmDel.id;
    try { await api.delete(`/recipes/${id}`); toast.success("Resep & SOP dihapus"); setConfirmDel(null); if (view === "detail") setView("list"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // dynamic ingredients
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: "", qty: "", unit: "" }] }));
  const setIng = (i, k, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const delIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, j) => j !== i) }));
  // dynamic steps
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { text: "", photo: "" }] }));
  const setStep = (i, k, v) => setForm((f) => ({ ...f, steps: f.steps.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const delStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));
  const reorderStep = (from, to) => setForm((f) => { const s = [...f.steps]; const [m] = s.splice(from, 1); s.splice(to, 0, m); return { ...f, steps: s }; });
  // dynamic QC
  const addQc = () => setForm((f) => ({ ...f, qc: [...f.qc, ""] }));
  const setQc = (i, v) => setForm((f) => ({ ...f, qc: f.qc.map((x, j) => (j === i ? v : x)) }));
  const delQc = (i) => setForm((f) => ({ ...f, qc: f.qc.filter((_, j) => j !== i) }));

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    return (fcat === "all" || r.category === fcat) && (!s || (r.name || "").toLowerCase().includes(s) || (r.description || "").toLowerCase().includes(s));
  });

  if (view === "form") return <RecipeForm {...{ form, setForm, editId, save, saving, setView, addIng, setIng, delIng, addStep, setStep, delStep, reorderStep, addQc, setQc, delQc, dragIdx }} />;
  if (view === "detail" && detail) return <RecipeDetail rec={detail} onBack={() => setView("list")} onEdit={() => openEdit(detail)} onDelete={() => setConfirmDel(detail)} confirmDel={confirmDel} setConfirmDel={setConfirmDel} doDelete={doDelete} />;

  return (
    <div className="space-y-4" data-testid="recipe-panel">
      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><ChefHat className="text-crimson" size={18} /> RESEP DAN SOP ({rows.length})</div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative flex-1 sm:w-60">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk" className="bg-neutral-100 border-neutral-200 pl-9" />
            </div>
            <Select value={fcat} onValueChange={setFcat}>
              <SelectTrigger data-testid="recipe-filter" className="w-44 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="recipe-add-btn" onClick={openAdd} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah</Button>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="recipe-list">
          {filtered.length === 0 && <p className="col-span-full text-sm text-muted-foreground">{rows.length === 0 ? "Belum ada resep & SOP." : "Tidak ada hasil."}</p>}
          {filtered.map((r) => (
            <Card key={r.id} data-testid={`recipe-card-${r.id}`} className="overflow-hidden rounded-2xl border border-neutral-200">
              <div className="h-36 w-full bg-neutral-100">
                {r.photo_final || r.photo_product ? <img src={photoUrl(r.photo_final || r.photo_product)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-neutral-300"><ChefHat size={40} /></div>}
              </div>
              <div className="p-3">
                <div className="font-display font-bold text-neutral-900">{r.name}</div>
                <div className="mb-2 inline-flex rounded-full bg-crimson/10 px-2 py-0.5 text-[10px] font-bold text-crimson">{r.category}</div>
                <div className="flex gap-1">
                  <Button data-testid={`recipe-view-${r.id}`} onClick={() => openDetail(r)} variant="outline" size="sm" className="flex-1 border-neutral-300 text-xs font-bold"><Eye size={14} className="mr-1" /> Lihat</Button>
                  <Button data-testid={`recipe-edit-${r.id}`} onClick={() => openEdit(r)} variant="ghost" size="icon" className="text-gold hover:bg-gold/10"><Pencil size={16} /></Button>
                  <Button data-testid={`recipe-del-${r.id}`} onClick={() => setConfirmDel(r)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
      <DelConfirm rec={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />
    </div>
  );
}

function DelConfirm({ rec, onCancel, onConfirm }) {
  if (!rec) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="recipe-del-confirm">
      <Card className="w-full max-w-sm rounded-2xl p-6">
        <div className="font-display text-lg font-bold text-neutral-900">Hapus Resep & SOP?</div>
        <p className="mt-1 text-sm text-muted-foreground">Apakah Anda yakin ingin menghapus "{rec.name}"? Tindakan ini tidak dapat dibatalkan.</p>
        <div className="mt-4 flex gap-2">
          <Button data-testid="recipe-del-cancel" onClick={onCancel} variant="outline" className="flex-1 border-neutral-300 font-bold">Batal</Button>
          <Button data-testid="recipe-del-confirm-btn" onClick={onConfirm} className="flex-1 bg-crimson font-bold hover:bg-red-500">Hapus</Button>
        </div>
      </Card>
    </div>
  );
}

function RecipeForm({ form, setForm, editId, save, saving, setView, addIng, setIng, delIng, addStep, setStep, delStep, reorderStep, addQc, setQc, delQc, dragIdx }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4" data-testid="recipe-form">
      <div className="flex items-center justify-between">
        <Button onClick={() => setView("list")} variant="ghost" className="font-bold text-neutral-900"><ArrowLeft size={18} className="mr-1" /> Kembali</Button>
        <Button data-testid="recipe-save-btn" disabled={saving} onClick={save} className="bg-crimson font-bold hover:bg-red-500">{saving ? "Menyimpan…" : editId ? "Perbarui" : "Simpan"}</Button>
      </div>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 font-display font-bold text-neutral-900">Informasi Produk</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input data-testid="rf-name" placeholder="Nama Produk" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-neutral-100 border-neutral-200" />
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="rf-category" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="rf-batch" placeholder="Hasil Produksi / Batch (mis. 10 Liter)" value={form.batch} onChange={(e) => set("batch", e.target.value)} className="bg-neutral-100 border-neutral-200 sm:col-span-2" />
          <Textarea data-testid="rf-desc" placeholder="Deskripsi" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-neutral-100 border-neutral-200 sm:col-span-2" />
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 font-display font-bold text-neutral-900">Foto</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <PhotoSlot testid="rf-photo-product" label="Foto Bahan / Produk" value={form.photo_product} onChange={(v) => set("photo_product", v)} />
          <PhotoSlot testid="rf-photo-final" label="Foto Hasil Akhir" value={form.photo_final} onChange={(v) => set("photo_final", v)} />
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 flex items-center justify-between"><span className="font-display font-bold text-neutral-900">Bahan & Resep</span><Button data-testid="rf-add-ing" onClick={addIng} size="sm" variant="outline" className="border-neutral-300 font-bold"><Plus size={14} className="mr-1" /> Tambah Bahan</Button></div>
        <div className="space-y-2" data-testid="rf-ingredients">
          {form.ingredients.length === 0 && <p className="text-xs text-muted-foreground">Belum ada bahan.</p>}
          {form.ingredients.map((ing, i) => (
            <div key={i} className="flex gap-2" data-testid={`rf-ing-${i}`}>
              <Input placeholder="Nama Bahan" value={ing.name} onChange={(e) => setIng(i, "name", e.target.value)} className="flex-1 bg-neutral-100 border-neutral-200" />
              <Input placeholder="Jumlah" value={ing.qty} onChange={(e) => setIng(i, "qty", e.target.value)} className="w-24 bg-neutral-100 border-neutral-200" />
              <Input placeholder="Satuan" value={ing.unit} onChange={(e) => setIng(i, "unit", e.target.value)} className="w-28 bg-neutral-100 border-neutral-200" />
              <Button onClick={() => delIng(i)} variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-1 flex items-center justify-between"><span className="font-display font-bold text-neutral-900">Langkah Pembuatan</span><Button data-testid="rf-add-step" onClick={addStep} size="sm" variant="outline" className="border-neutral-300 font-bold"><Plus size={14} className="mr-1" /> Tambah Langkah</Button></div>
        <p className="mb-3 text-[11px] text-muted-foreground">Seret ikon ⋮⋮ untuk mengubah urutan langkah.</p>
        <div className="space-y-2" data-testid="rf-steps">
          {form.steps.length === 0 && <p className="text-xs text-muted-foreground">Belum ada langkah.</p>}
          {form.steps.map((st, i) => (
            <div key={i} data-testid={`rf-step-${i}`} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragIdx.current !== null && dragIdx.current !== i) reorderStep(dragIdx.current, i); dragIdx.current = null; }} className="flex gap-2 rounded-xl bg-neutral-100 p-2">
              <div className="flex flex-col items-center gap-1 pt-1"><GripVertical size={16} className="cursor-grab text-muted-foreground" /><span className="flex h-6 w-6 items-center justify-center rounded-full bg-crimson text-xs font-bold text-white">{i + 1}</span></div>
              <div className="flex-1 space-y-2">
                <Textarea placeholder={`Instruksi Langkah ${i + 1}`} value={st.text} onChange={(e) => setStep(i, "text", e.target.value)} className="min-h-[60px] bg-white border-neutral-200" />
                <PhotoSlot testid={`rf-step-photo-${i}`} label="Foto Langkah (opsional)" value={st.photo} onChange={(v) => setStep(i, "photo", v)} />
              </div>
              <Button onClick={() => delStep(i)} variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 flex items-center justify-between"><span className="flex items-center gap-2 font-display font-bold text-neutral-900"><ListChecks size={16} className="text-crimson" /> Quality Control</span><Button data-testid="rf-add-qc" onClick={addQc} size="sm" variant="outline" className="border-neutral-300 font-bold"><Plus size={14} className="mr-1" /> Tambah QC</Button></div>
        <div className="space-y-2" data-testid="rf-qc">
          {form.qc.length === 0 && <p className="text-xs text-muted-foreground">Belum ada item QC (mis. Rasa, Warna, Aroma, Berat, Tampilan).</p>}
          {form.qc.map((c, i) => (
            <div key={i} className="flex gap-2" data-testid={`rf-qc-${i}`}>
              <Input placeholder="Item QC (mis. Rasa)" value={c} onChange={(e) => setQc(i, e.target.value)} className="flex-1 bg-neutral-100 border-neutral-200" />
              <Button onClick={() => delQc(i)} variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 font-display font-bold text-neutral-900">Penyimpanan</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select value={form.storage_location} onValueChange={(v) => set("storage_location", v)}>
            <SelectTrigger data-testid="rf-storage" className="bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
            <SelectContent>{STORAGE_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="rf-shelf" placeholder="Masa Simpan (mis. 3 hari)" value={form.shelf_life} onChange={(e) => set("shelf_life", e.target.value)} className="bg-neutral-100 border-neutral-200" />
          <Textarea data-testid="rf-notes" placeholder="Catatan" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-neutral-100 border-neutral-200 sm:col-span-2" />
        </div>
      </Card>

      <div className="flex justify-end pb-6">
        <Button data-testid="recipe-save-btn2" disabled={saving} onClick={save} className="bg-crimson font-bold hover:bg-red-500">{saving ? "Menyimpan…" : editId ? "Perbarui" : "Simpan"}</Button>
      </div>
    </div>
  );
}

const imgToData = (url) => new Promise((res) => { fetch(url).then((r) => r.blob()).then((b) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(b); }).catch(() => res(null)); });

function RecipeDetail({ rec, onBack, onEdit, onDelete, confirmDel, setConfirmDel, doDelete }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPDF = async () => {
    setPdfBusy(true);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const W = 210, M = 14; let y = 14;
      const logo = await imgToData(`${window.location.origin}/jjl-logo.png`);
      if (logo) { try { doc.addImage(logo, "PNG", M, y, 16, 16); } catch (e) {} }
      doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(200, 30, 30);
      doc.text("DA LE TANG HuoGuo", M + 20, y + 7);
      doc.setFontSize(10); doc.setTextColor(90); doc.setFont("helvetica", "normal");
      doc.text("Resep & SOP Operasional", M + 20, y + 13);
      y += 22; doc.setDrawColor(220); doc.line(M, y, W - M, y); y += 8;
      doc.setFontSize(18); doc.setTextColor(20); doc.setFont("helvetica", "bold"); doc.text(rec.name || "-", M, y); y += 7;
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(90);
      doc.text(`Kategori: ${rec.category || "-"}    Batch: ${rec.batch || "-"}`, M, y); y += 6;
      if (rec.description) { const dl = doc.splitTextToSize(rec.description, W - 2 * M); doc.text(dl, M, y); y += dl.length * 5 + 2; }
      // photos
      const pics = [];
      if (rec.photo_product) { const d = await imgToData(photoUrl(rec.photo_product)); if (d) pics.push(["Foto Bahan/Produk", d]); }
      if (rec.photo_final) { const d = await imgToData(photoUrl(rec.photo_final)); if (d) pics.push(["Foto Hasil Akhir", d]); }
      if (pics.length) {
        let x = M; const iw = 56, ih = 42;
        pics.forEach(([lbl, d]) => { doc.setFontSize(8); doc.setTextColor(120); doc.text(lbl, x, y); try { doc.addImage(d, "JPEG", x, y + 2, iw, ih); } catch (e) {} x += iw + 6; });
        y += ih + 8;
      }
      const ensure = (h) => { if (y + h > 285) { doc.addPage(); y = 16; } };
      // ingredients
      ensure(12); doc.setFontSize(12); doc.setTextColor(200, 30, 30); doc.setFont("helvetica", "bold"); doc.text("Bahan & Resep", M, y); y += 6;
      doc.setFontSize(9); doc.setTextColor(30); doc.setFont("helvetica", "normal");
      (rec.ingredients || []).forEach((ing) => { ensure(6); doc.text(`• ${ing.name || "-"}`, M, y); doc.text(`${ing.qty || ""} ${ing.unit || ""}`, W - M, y, { align: "right" }); y += 5; });
      y += 4;
      // steps
      ensure(12); doc.setFontSize(12); doc.setTextColor(200, 30, 30); doc.setFont("helvetica", "bold"); doc.text("Langkah Pembuatan", M, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setTextColor(30); doc.setFontSize(9);
      for (let i = 0; i < (rec.steps || []).length; i++) {
        const st = rec.steps[i]; const lines = doc.splitTextToSize(`${i + 1}. ${st.text || ""}`, W - 2 * M);
        let sp = null; if (st.photo) sp = await imgToData(photoUrl(st.photo));
        ensure(lines.length * 5 + (sp ? 34 : 2)); doc.text(lines, M, y); y += lines.length * 5;
        if (sp) { try { doc.addImage(sp, "JPEG", M + 4, y, 44, 30); } catch (e) {} y += 34; }
        y += 2;
      }
      y += 2;
      // QC
      if ((rec.qc || []).length) { ensure(12); doc.setFontSize(12); doc.setTextColor(200, 30, 30); doc.setFont("helvetica", "bold"); doc.text("Quality Control", M, y); y += 6; doc.setFont("helvetica", "normal"); doc.setTextColor(30); doc.setFontSize(9); rec.qc.forEach((c) => { ensure(6); doc.text(`☐ ${c}`, M, y); y += 5; }); y += 4; }
      // storage
      ensure(20); doc.setFontSize(12); doc.setTextColor(200, 30, 30); doc.setFont("helvetica", "bold"); doc.text("Penyimpanan", M, y); y += 6;
      doc.setFont("helvetica", "normal"); doc.setTextColor(30); doc.setFontSize(9);
      doc.text(`Lokasi: ${rec.storage_location || "-"}    Masa Simpan: ${rec.shelf_life || "-"}`, M, y); y += 5;
      if (rec.notes) { const nl = doc.splitTextToSize(`Catatan: ${rec.notes}`, W - 2 * M); ensure(nl.length * 5); doc.text(nl, M, y); }
      doc.save(`Resep-${(rec.name || "sop").replace(/\s+/g, "_")}.pdf`);
    } catch (e) { toast.error("Gagal membuat PDF"); } finally { setPdfBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="recipe-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button onClick={onBack} variant="ghost" className="font-bold text-neutral-900"><ArrowLeft size={18} className="mr-1" /> Kembali</Button>
        <div className="flex gap-2">
          <Button data-testid="detail-edit-btn" onClick={onEdit} variant="outline" className="border-gold/50 font-bold text-gold hover:bg-gold/10"><Pencil size={16} className="mr-1" /> Edit</Button>
          <Button data-testid="detail-del-btn" onClick={onDelete} variant="outline" className="border-crimson/40 font-bold text-crimson hover:bg-crimson/10"><Trash2 size={16} className="mr-1" /> Hapus</Button>
          <Button data-testid="detail-pdf-btn" disabled={pdfBusy} onClick={exportPDF} className="bg-crimson font-bold hover:bg-red-500"><FileDown size={16} className="mr-1" /> {pdfBusy ? "Membuat…" : "Cetak PDF"}</Button>
        </div>
      </div>
      <Card className="card-surface rounded-2xl p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {rec.photo_product && <img src={photoUrl(rec.photo_product)} alt="" className="h-48 w-full rounded-xl object-cover" />}
          {rec.photo_final && <img src={photoUrl(rec.photo_final)} alt="" className="h-48 w-full rounded-xl object-cover" />}
        </div>
        <div className="mt-4 font-display text-2xl font-extrabold text-neutral-900">{rec.name}</div>
        <div className="mt-1 flex flex-wrap gap-2 text-sm">
          <span className="rounded-full bg-crimson/10 px-2 py-0.5 text-xs font-bold text-crimson">{rec.category}</span>
          {rec.batch && <span className="text-muted-foreground">Batch: {rec.batch}</span>}
        </div>
        {rec.description && <p className="mt-2 text-sm text-neutral-700">{rec.description}</p>}
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-2 font-display font-bold text-neutral-900">Bahan & Resep</div>
        {(rec.ingredients || []).length === 0 ? <p className="text-sm text-muted-foreground">-</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Nama Bahan</th><th className="py-1">Jumlah</th><th className="py-1">Satuan</th></tr></thead>
            <tbody>{rec.ingredients.map((ing, i) => <tr key={i} className="border-t border-neutral-100"><td className="py-1.5 font-medium text-neutral-900">{ing.name}</td><td className="py-1.5">{ing.qty}</td><td className="py-1.5">{ing.unit}</td></tr>)}</tbody>
          </table>
        )}
      </Card>

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-3 font-display font-bold text-neutral-900">Langkah Pembuatan</div>
        <div className="space-y-3">
          {(rec.steps || []).map((st, i) => (
            <div key={i} className="flex gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-crimson text-sm font-bold text-white">{i + 1}</span>
              <div className="flex-1">
                <p className="text-sm text-neutral-800">{st.text}</p>
                {st.photo && <img src={photoUrl(st.photo)} alt="" className="mt-2 h-32 rounded-lg object-cover" />}
              </div>
            </div>
          ))}
          {(rec.steps || []).length === 0 && <p className="text-sm text-muted-foreground">-</p>}
        </div>
      </Card>

      {(rec.qc || []).length > 0 && (
        <Card className="card-surface rounded-2xl p-5">
          <div className="mb-2 font-display font-bold text-neutral-900">Quality Control</div>
          <ul className="space-y-1">{rec.qc.map((c, i) => <li key={i} className="flex items-center gap-2 text-sm text-neutral-800"><span className="flex h-4 w-4 items-center justify-center rounded border border-neutral-400 text-[10px]">☐</span> {c}</li>)}</ul>
        </Card>
      )}

      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-2 font-display font-bold text-neutral-900">Penyimpanan</div>
        <div className="text-sm text-neutral-800">Lokasi: <b>{rec.storage_location || "-"}</b> · Masa Simpan: <b>{rec.shelf_life || "-"}</b></div>
        {rec.notes && <p className="mt-1 text-sm text-muted-foreground">Catatan: {rec.notes}</p>}
      </Card>

      <DelConfirm rec={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />
    </div>
  );
}
